import React from "react"
import { useEffect, useState } from "react"
import { navigate } from "gatsby"
import { useStore } from "../../context/auth/auth"
import { formatRegions } from "../config/regionConfig"
import { profilesBaseUrl } from "../config/ecomConfig"
import getConfigTID from "../functions/getConfigTID"
import { refreshAuthToken } from "../functions/refreshAuthToken"
import { Dialog } from "trimble-terra"

const {
  redirect_uri,
  code_verifier,
  client_id,
  url,
  client_secret,
} = getConfigTID()

const languageDefault = {
  nativeField: "English",
  translatableName: "",
  code: "en",
  localeCode: "en-US",
}

const useLanguage = (
  location,
  region,
  regionList,
  languages,
  pageType = null,
) => {
  const [state] = useStore()
  const [profile, setProfile] = useState(null)
  const [languageSettings, setLanguageSettings] = useState(null)

  const getLogin = () => {
    const login = JSON.parse(localStorage?.getItem("user-trial-data"))
    const accessToken = login?.id
    const userInfo = JSON.parse(localStorage.getItem("user_info"))
    const uuid = userInfo?.tid
    if (accessToken && uuid) {
      return {
        accessToken,
        uuid,
      }
    }
    return null
  }

  useEffect(() => {
    if (pageType) {
      return
    }
    if (typeof window !== "undefined") {
      if (languageSettings) {
        const languageCodeInUrl =
          window?.location?.pathname?.split("/")?.length &&
          window?.location?.pathname?.split("/")?.length >= 1
            ? window?.location?.pathname?.split("/")[1]
            : ""
        const languageCode = languageSettings?.language?.code?.toLowerCase()

        if (languageCodeInUrl !== languageCode) {
          let url = window?.location?.pathname
          let result = url.replace(languageCodeInUrl, languageCode)
          navigate(result)
        }
      }
    }
  }, [languageSettings, pageType])

  useEffect(() => {
    if (typeof window !== "undefined") {
      const currentUrl = new URL(window?.location)
      const urlLangCode = currentUrl.searchParams.get("lang")
      if (urlLangCode) {
        let updatedLanguage
        languages?.forEach(language => {
          if (language?.code === urlLangCode) {
            // if language in ?lang param matches real language code, assign that as the new language
            updatedLanguage = language
          }
        })

        if (
          updatedLanguage?.nativeField !== undefined &&
          updatedLanguage?.code !== undefined
        ) {
          // update language selector with new language if the fields exist
          saveLanguageSettings({
            region: region?.region
              ? region.region
              : region?.name
              ? region.name
              : "",
            machineName: region?.machineName,
            language: updatedLanguage,
          })
        } else {
          // if lang value doesn't match, remove it from the URL and reload original page
          currentUrl.searchParams.delete("lang")
          navigate(`${currentUrl.toString()}`, { replace: true })
        }
      }
    }
  })

  const updateProfileLanguage = async (newLanguage) => {
    const login = getLogin()
    if (login) {
      const languageCode = newLanguage?.language?.localeCode
      if (languageCode) {
        return fetch(
          `${profilesBaseUrl}/profiles/users/${login?.uuid}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${login?.accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              language: languageCode,
            }),
          }
        )
        .then(response => {
          if (response.status === 401) {
            refreshAuthToken(
              client_id,
              client_secret,
              redirect_uri,
              code_verifier,
              null,
              url,
              null,
              null,
              () => updateProfileLanguage(newLanguage),
              null
            )
          }
        })
        .catch(error => {
          console.log("error", error)
        })
      }
    }
  }

  const saveLanguageSettings = async (newLanguage) => {
    if (newLanguage) {

      await updateProfileLanguage(newLanguage)

      if (!newLanguage.machineName && formatRegions.length) {
        const y = formatRegions(regionList)?.find(reg => {
          return reg.name.toLowerCase() === newLanguage.region.toLowerCase()
        })
        if (y) {
          newLanguage["machineName"] = y.machineName
        }
      }
      newLanguage.language["code"] = newLanguage.language.code.toLowerCase()

      localStorage.setItem("languageSettings", JSON.stringify(newLanguage))

      let path = ""
      if (location?.pathname) {
        const slashIndex = location?.pathname?.indexOf("/", 1)
        path = slashIndex !== -1 ? location?.pathname?.slice(slashIndex) : ""
      }

      navigate(`/${newLanguage?.language?.code?.toLowerCase()}${path}`, {
        replace: true,
      })
    }
  }

  useEffect(() => {
    const fetchProfile = async () => {
      const accessToken = JSON.parse(
        localStorage.getItem("user-trial-data")
      )?.id
      if (accessToken) {
        fetch(
          `${profilesBaseUrl}/profiles/users/me`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        )
          .then(response => response.json())
          .then(data => {
            setProfile(data)
          })
          .catch(error => {
            console.log("error", error)
          })
      }
    }

    fetchProfile()
  }, [state?.isAuthenticationComplete])

  useEffect(() => {
    const languageSettings = JSON.parse(
      localStorage.getItem("languageSettings")
    )

    const languageCode = profile?.language
      ? profile.language
      : languageSettings?.language?.localeCode
      ? languageSettings?.language?.localeCode
      : navigator?.language
      ? navigator.language
      : "en-US"

    const language = languages?.find(languageEntry => languageEntry?.localeCode === languageCode) || languageDefault

    const newLanguageSettings = {
      region: region?.region ? region.region : region?.name ? region.name : "",
      machineName: region?.machineName,
      language: language,
    }

    setLanguageSettings(newLanguageSettings)
    localStorage.setItem(
      "languageSettings",
      JSON.stringify(newLanguageSettings)
    )
  }, [profile, languages, region, state.isAuthenticationComplete])

  return { languageSettings, saveLanguageSettings }
}

export default useLanguage
